import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { DECKHUB } from "@/constants/testIds";
import { HOUSES, HOUSE_ORDER } from "@/lib/houses";
import { useSoulProfile, setCachedHouse } from "@/lib/useSoulProfile";
import HouseCard from "@/components/HouseCard";
import FounderBadge from "@/components/FounderBadge";
import { X, Check } from "lucide-react";

const SEGMENTS = [
  { id: "mine", label: "My Deck" },
  { id: "houses", label: "House Decks" },
  { id: "editions", label: "Physical Editions" },
];

export default function SoulDeckHub() {
  const [segment, setSegment] = useState("mine");
  const [deck, setDeck] = useState(null);
  const [editions, setEditions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [shipping, setShipping] = useState({ name: "", address: "", city: "", postal: "", country: "" });
  const [placing, setPlacing] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [dailyCard, setDailyCard] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [committing, setCommitting] = useState(false);
  const { element, dominant, chosen: profileChosen } = useSoulProfile();

  useEffect(() => {
    setSelectedHouse(element);
    setChosen(profileChosen);
  }, [element, profileChosen]);

  useEffect(() => {
    api.get("/deck/mine").then((r) => setDeck(r.data)).catch(() => {});
    api.get("/commerce/editions").then((r) => setEditions(r.data.editions)).catch(() => {});
    api.get("/commerce/orders").then((r) => setOrders(r.data.orders)).catch(() => {});
    api.get("/oracle/card-of-day").then((r) => setDailyCard(r.data.card)).catch(() => {});
  }, []);

  const commitHouse = async () => {
    setCommitting(true);
    try {
      await api.post("/soul/house", { house: selectedHouse });
      setCachedHouse(selectedHouse);
      setChosen(selectedHouse);
      toast.success(`The ${selectedHouse} House now carries your readings.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The house could not be chosen.");
    } finally {
      setCommitting(false);
    }
  };

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/commerce/checkout", {
        edition_id: checkout.id,
        shipping,
      });
      toast.success("Order placed. (MOCK — no card was charged.)");
      setOrders((cur) => [data.order, ...cur]);
      setCheckout(null);
      setShipping({ name: "", address: "", city: "", postal: "", country: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order.");
    } finally {
      setPlacing(false);
    }
  };

  const house = HOUSES[selectedHouse || "Fire"];

  return (
    <div
      data-testid={DECKHUB.page}
      className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20"
    >
      <div className="relative max-w-6xl mx-auto">
        <div className="mb-10">
          <div className="font-overline mb-4">Soul Deck</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
            Your artifact,
            <br />
            in every form.
          </h1>
        </div>

        <div className="mb-12 flex gap-8 border-b border-[#C8A96E]/15 pb-3 overflow-x-auto">
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              data-testid={DECKHUB.segment(s.id)}
              onClick={() => setSegment(s.id)}
              className={`font-overline whitespace-nowrap transition-colors ${
                segment === s.id ? "text-[#C8A96E]" : "text-[#F4EFE6]/50 hover:text-[#F4EFE6]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {segment === "mine" && (
            <motion.section
              key="mine"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
            >
              {!deck ? (
                <div className="font-heading text-lg text-[#F4EFE6]/60">Your deck has not been drawn yet.</div>
              ) : (
                <>
                  <div className="font-overline text-[#C8A96E] mb-8">
                    {deck.element} current · {deck.cards.length} cards
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 sm:gap-10">
                    {deck.cards.map((c) => (
                      <div key={c.index} className="flex flex-col items-center">
                        <div className="w-full aspect-square rounded-2xl overflow-hidden card-frame">
                          <img
                            src={mediaUrl(c.image_url)}
                            alt={c.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="pt-4 font-heading italic text-base sm:text-lg text-[#F4EFE6] text-center">
                          {c.title}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </motion.section>
          )}

          {segment === "houses" && (
            <motion.section
              key="houses"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="font-overline text-[#F4EFE6]/60 mb-8">
                Your dominant current is{" "}
                <span className="text-[#C8A96E]">{dominant}</span>. Preview any house on
                today&apos;s reading before you choose.
              </div>

              {/* Featured — swap preview on the seeker's own reading */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_1fr] gap-12 lg:gap-16 items-center mb-20">
                <div className="flex flex-col items-center">
                  {dailyCard ? (
                    <>
                      <HouseCard
                        key={selectedHouse}
                        element={selectedHouse || "Fire"}
                        faceUrl={dailyCard.image_url}
                        upright={dailyCard.upright}
                        delay={1.2}
                        size="lg"
                        dataTestid={DECKHUB.housePreviewCard}
                      />
                      <div className="mt-4 font-overline text-[#F4EFE6]/40 text-center">
                        Today&apos;s card · {dailyCard.title}
                      </div>
                    </>
                  ) : (
                    <motion.div
                      key={selectedHouse}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 1.2, ease: [0.25, 0.6, 0.3, 1] }}
                      className="w-full max-w-md aspect-square rounded-2xl overflow-hidden"
                      style={{ boxShadow: "0 30px 90px rgba(0,0,0,0.6)" }}
                    >
                      <img
                        src={house.back}
                        alt={`${house.name} House card back`}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </motion.div>
                  )}
                </div>
                <div>
                  <div className="font-overline text-[#C8A96E] mb-3">The {house.name} House</div>
                  <div className="font-heading italic text-3xl sm:text-4xl text-[#F4EFE6] mb-4 leading-tight">
                    {house.descriptor}
                  </div>
                  <p className="font-heading text-lg text-[#F4EFE6]/70 leading-relaxed max-w-lg">
                    {dailyCard
                      ? "This is today's card arriving through the " +
                        house.name +
                        " House. Swap houses below to feel each current on your own reading."
                      : "Every card drawn for you will arrive on this card back — the production artwork of your House. Preserved exactly as painted."}
                  </p>
                  <div className="mt-8">
                    {selectedHouse === (chosen || dominant) ? (
                      <div
                        data-testid={DECKHUB.chosenHouseBadge}
                        className="inline-flex items-center gap-2 font-overline text-[#C8A96E] border border-[#C8A96E]/40 px-4 py-3"
                      >
                        <Check size={14} strokeWidth={1.5} />
                        {chosen === selectedHouse
                          ? "Your chosen House — carrying your readings"
                          : "Your dominant House — carrying your readings"}
                      </div>
                    ) : (
                      <button
                        data-testid={DECKHUB.chooseHouseBtn}
                        className="btn-sacred btn-sacred-solid"
                        disabled={committing}
                        onClick={commitHouse}
                      >
                        {committing ? "Choosing…" : `Choose the ${selectedHouse} House`}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* All 4 houses */}
              <div className="font-overline mb-6">All Houses</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {HOUSE_ORDER.map((el) => {
                  const h = HOUSES[el];
                  const active = selectedHouse === el;
                  const isDominant = dominant === el;
                  const isChosen = chosen === el;
                  return (
                    <button
                      key={el}
                      data-testid={DECKHUB.houseTile(el)}
                      onClick={() => setSelectedHouse(el)}
                      className="flex flex-col items-center group"
                    >
                      <motion.div
                        whileHover={{ y: -4 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className={`w-full aspect-square rounded-2xl overflow-hidden transition-all ${
                          active ? "ring-1 ring-[#C8A96E]" : "opacity-70 group-hover:opacity-100"
                        }`}
                        style={{ boxShadow: "0 16px 40px rgba(0,0,0,0.5)" }}
                      >
                        <img
                          src={h.back}
                          alt={`${h.name} House`}
                          className="w-full h-full object-cover"
                          draggable={false}
                        />
                      </motion.div>
                      <div className="mt-3 font-overline text-[#F4EFE6]/70 group-hover:text-[#F4EFE6]">
                        {h.name}
                        {isChosen && <span className="text-[#C8A96E] ml-2">· chosen</span>}
                        {!isChosen && isDominant && (
                          <span className="text-[#C8A96E]/70 ml-2">· dominant</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.section>
          )}

          {segment === "editions" && (
            <motion.section
              key="editions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {editions.map((e) => (
                  <div
                    key={e.id}
                    data-testid={DECKHUB.editionCard(e.id)}
                    className={`card-frame p-8 relative flex flex-col ${
                      e.founder_only ? "border-[#C8A96E]/50" : ""
                    }`}
                  >
                    {e.founder_only && (
                      <div className="absolute -top-3 left-6">
                        <FounderBadge />
                      </div>
                    )}
                    <div className="font-heading text-3xl italic text-[#F4EFE6] mb-3">{e.name}</div>
                    <div className="font-heading text-4xl text-[#C8A96E] font-light mb-2">${e.price_usd}</div>
                    <p className="text-sm text-[#F4EFE6]/60 leading-relaxed mb-6">{e.description}</p>
                    <ul className="space-y-2 mb-8 flex-1">
                      {e.perks.map((perk, i) => (
                        <li key={i} className="text-sm text-[#F4EFE6]/80 flex items-start gap-2">
                          <span className="text-[#C8A96E] mt-1">·</span> {perk}
                        </li>
                      ))}
                    </ul>
                    {e.founder_only && (
                      <div className="font-overline text-[#F4EFE6]/50 mb-4">
                        {e.remaining} of {e.quantity_cap} remaining
                      </div>
                    )}
                    <button
                      data-testid={DECKHUB.orderBtn(e.id)}
                      className={`btn-sacred ${e.founder_only ? "btn-sacred-solid" : ""} w-full`}
                      disabled={!e.available}
                      onClick={() => setCheckout(e)}
                    >
                      {e.available ? "Order" : "Sold out"}
                    </button>
                  </div>
                ))}
              </div>

              {orders.length > 0 && (
                <div className="mt-16">
                  <div className="font-overline mb-4">Your orders</div>
                  <div className="space-y-3">
                    {orders.map((o) => (
                      <div key={o.order_id} className="card-frame p-4 flex items-center justify-between">
                        <div>
                          <div className="font-heading italic text-lg text-[#F4EFE6] flex items-center gap-3">
                            {o.edition_name}
                            {o.founder_number && <FounderBadge number={o.founder_number} />}
                          </div>
                          <div className="font-overline text-[#F4EFE6]/40">
                            {o.order_id} · ${o.price_usd}
                          </div>
                        </div>
                        <div className="font-overline text-[#C8A96E]">
                          {o.status.replace("_", " ")}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-6 font-overline text-[#F4EFE6]/40 italic">
                    Payment is MOCKED · no card is charged in this preview build.
                  </p>
                </div>
              )}
            </motion.section>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {checkout && (
            <motion.div
              data-testid={DECKHUB.checkoutModal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
              onClick={() => setCheckout(null)}
            >
              <motion.div
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="card-frame w-full max-w-lg p-8 sm:p-10 bg-[#0A0A0A]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="font-overline">Ceremonial order · MOCK</div>
                    <div className="font-heading italic text-3xl text-[#F4EFE6] mt-1">{checkout.name}</div>
                    <div className="font-heading text-2xl text-[#C8A96E] font-light">${checkout.price_usd}</div>
                  </div>
                  <button
                    data-testid={DECKHUB.checkoutCancel}
                    onClick={() => setCheckout(null)}
                    className="text-[#F4EFE6]/50 hover:text-[#C8A96E]"
                  >
                    <X />
                  </button>
                </div>
                <div className="space-y-5">
                  <div>
                    <label className="font-overline block mb-2">Name</label>
                    <input
                      data-testid={DECKHUB.checkoutName}
                      className="input-sacred"
                      value={shipping.name}
                      onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="font-overline block mb-2">Address</label>
                    <input
                      data-testid={DECKHUB.checkoutAddress}
                      className="input-sacred"
                      value={shipping.address}
                      onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-overline block mb-2">City</label>
                      <input
                        data-testid={DECKHUB.checkoutCity}
                        className="input-sacred"
                        value={shipping.city}
                        onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="font-overline block mb-2">Postal</label>
                      <input
                        data-testid={DECKHUB.checkoutPostal}
                        className="input-sacred"
                        value={shipping.postal}
                        onChange={(e) => setShipping({ ...shipping, postal: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-overline block mb-2">Country</label>
                    <input
                      data-testid={DECKHUB.checkoutCountry}
                      className="input-sacred"
                      value={shipping.country}
                      onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                    />
                  </div>
                </div>
                <p className="mt-6 font-overline text-[#F4EFE6]/40">
                  This preview build does not charge a real payment.
                </p>
                <div className="mt-8 flex justify-end gap-4">
                  <button className="btn-sacred" onClick={() => setCheckout(null)}>
                    Cancel
                  </button>
                  <button
                    data-testid={DECKHUB.checkoutSubmit}
                    className="btn-sacred btn-sacred-solid"
                    disabled={placing || !shipping.name || !shipping.address}
                    onClick={placeOrder}
                  >
                    {placing ? "Placing…" : "Place ceremonial order"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
